import { h as __h } from "preact";
import { Component, createRef } from "preact";
/**
 * @import {
 *   ComponentChild,
 *   ComponentChildren,
 *   RefObject
 * } from "preact"
 */

import Float from "~/util/float.js";
/** @import { Placement } from "~/util/float.js"; */
import html from "~/util/html.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Tooltip.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Tooltip.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * Spread these onto a `<button>`, even when it has no action; the tooltip also opens on focus,
 * and only a focusable trigger lets keyboard users reach it.
 *
 * @typedef {object} TriggerProps
 * @property {(el: HTMLElement) => () => void} ref
 * @property {string} aria-labelledby
 * @property {() => void} onFocusIn
 * @property {() => void} onFocusOut
 * @property {() => void} onPointerEnter
 * @property {() => void} onPointerLeave
 */

/**
 * @typedef {object} Props
 * @property {(props: TriggerProps) => ComponentChild} [children]
 * @property {ComponentChildren} contents
 * @property {Placement} [placement]
 * @property {string} [maxWidth]
 * @property {string} [offset]
 * @property {boolean} [disabled]
 * @property {number} [delay]
 */

/** @typedef {"focus" | "pointer"} InvokeType */

const warmth = {
  warm: false,
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  decay: undefined,
  onOpen() {
    this.warm = true;
    clearTimeout(this.decay);
    this.decay = undefined;
  },
  onClose() {
    const decayTime = 1500;
    clearTimeout(this.decay);
    this.decay = setTimeout(() => {
      this.warm = false;
      this.decay = undefined;
    }, decayTime);
  },
  /** @param {number} [delay] */
  getDelay(delay) {
    return this.warm ? 0 : delay;
  }
};

/** @extends {Component<Props>} */
export default class Tooltip extends Component {
  #id = crypto.randomUUID();

  /** @type {RefObject<HTMLDivElement>} */
  #ref = createRef();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timeout;
  /** @type {InvokeType | undefined} */
  #invokeType;
  get placement() {
    return this.props.placement ?? "top";
  }
  float = new Float({
    placement: this.placement
  });

  /** @param {InvokeType} invokeType */
  show(invokeType) {
    if (this.#invokeType) return;
    this.#invokeType = invokeType;
    const delay = warmth.getDelay(this.props.delay);
    if (!delay) this.#ref.current?.showPopover();else if (!this.#timeout) {
      this.#timeout = setTimeout(() => {
        this.#timeout = undefined;
        this.#ref.current?.showPopover();
      }, delay);
    }
  }

  /** @param {InvokeType} invokeType */
  hide(invokeType) {
    if (this.#invokeType !== invokeType) return;
    clearTimeout(this.#timeout);
    this.#timeout = undefined;
    if (this.#ref.current?.matches(":popover-open")) this.#ref.current.hidePopover();else this.#invokeType = undefined;
  }

  /** @override */
  componentWillUnmount() {
    clearTimeout(this.#timeout);
    this.#timeout = undefined;
  }

  /** @param {ToggleEvent} ev */
  #onToggle = ev => {
    if (ev.newState === "open") {
      if (this.props.delay) warmth.onOpen();
      this.float.show();
    } else {
      if (this.props.delay) warmth.onClose();
      this.#invokeType = undefined;
      clearTimeout(this.#timeout);
      this.#timeout = undefined;
      this.float.hide();
    }
  };

  /** @type {TriggerProps} */
  triggerProps = {
    ref: this.float.setReferenceRef,
    "aria-labelledby": this.#id,
    onFocusIn: () => this.show("focus"),
    onPointerEnter: () => this.show("pointer"),
    onFocusOut: () => this.hide("focus"),
    onPointerLeave: () => this.hide("pointer")
  };
  render() {
    /** @type {number | undefined} */
    let arrow;
    if (this.float.styles.arrow.left) arrow = Number.parseFloat(this.float.styles.arrow.left);
    if (this.float.styles.arrow.top) arrow = Number.parseFloat(this.float.styles.arrow.top);
    const style = {
      ...this.float.styles.float
    };
    if (this.props.maxWidth) style["--max-width"] = this.props.maxWidth;
    if (this.props.offset) style["--offset-size"] = this.props.offset;
    return [this.props.children?.(this.triggerProps), !this.props.disabled && __h("div", {
      "class": css.tooltip,
      id: this.#id,
      ref: (/** @type {HTMLDivElement} */el) => {
        this.#ref.current = el;
        const cleanup = this.float.setFloatingRef(el);
        return () => {
          this.#ref.current = null;
          cleanup();
        };
      },
      role: "tooltip",
      popover: "hint",
      onToggle: this.#onToggle,
      "data-side": this.float.side,
      "data-arrow": arrow,
      style: style
    }, __h("div", {
      "class": css.arrow,
      ref: this.float.setArrowRef,
      style: this.float.styles.arrow
    }), this.props.contents)];
  }
}